import { CSSProperties } from 'react'
import { formatCurrency, formatDateTime } from '@amusnet/amusnet-common'
import {
    gin,
    isFlexiBundle,
    isSingleOptionFlexiBundle,
    NotificationType,
    showFlexiBundles,
    showReactUINotification,
} from '@amusnet/react-ui'
import { translate } from '@amusnet/translations'
import { Translate } from '@amusnet/translations/react'
import { CountdownStrategy, BonusType } from '@amusnet/bonuses-http-client'
import { CountdownCounterDisplayItems } from '@components/CountdownFreeSpinsBonus/types'
import {
    useConfigurationStore,
    useBuyBonusStore,
    useFreeBuyBonusStore,
    useCountdownFSBonusStore,
    useFreeSpinsBonusesStore,
} from '@stores'
import { getCountdownDisplayItems, getCountdownValuesFromBonus, modifyFreeBuyBonuses } from '@utils/bonus'
import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'
import { GameUIMessageType, BonusesPlayNowMessage, BuyBonusFeature, BonusSystemState } from 'game-ui-types'
import { Bonus, FreeBuyBonusBonus, FreeSpinsBonus, GoldenChipsBonus } from '@amusnet/mgs-ws-client'

const FEATURE_IMAGE_PREFIX = 'buy-bonus-button-image-card-0'
const FALLBACK_IMAGE_NAME = 'buy-bonus-button-image-card-01.png'

const DISMISS_TIMEOUT_MS = 10000

const useBonusSystem = () => {
    // Configuration Store
    const hasBuyBonus = useConfigurationStore((state) => state.hasBuyBonus)
    const buyBonusConfig = useConfigurationStore((state) => state.buyBonusConfig)

    // Buy Bonus Global Settings Store
    const setSelectedFeatureIndex = useBuyBonusStore((state) => state.setSelectedFeatureIndex)
    const setSelectedFeature = useBuyBonusStore((state) => state.setSelectedFeature)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((state) => state.updateCurrentFeatureMultiplier)
    const setMultiChoiceToggle = useBuyBonusStore((state) => state.setMultiChoiceToggle)

    // Free Buy Bonus Store
    const setHasFBBActivatedBonus = useFreeBuyBonusStore((state) => state.setHasActivatedBonus)
    const setFBBActiveBonus = useFreeBuyBonusStore((state) => state.setActiveBonus)
    const setFBBonusSystemState = useFreeBuyBonusStore((state) => state.setBonusSystemState)
    const setFBBonuses = useFreeBuyBonusStore((state) => state.setBonuses)

    // Free Spins Store
    const setFSActiveBonus = useFreeSpinsBonusesStore((state) => state.setActiveBonus)
    const setFSBonusSystemState = useFreeSpinsBonusesStore((state) => state.setBonusSystemState)

    // Countdown Free Spins Store
    const setCFSActiveBonus = useCountdownFSBonusStore((state) => state.setActiveBonus)
    const setCFSBonusSystemState = useCountdownFSBonusStore((state) => state.setBonusSystemState)

    const showBonusAwardedNotification = (bonuses: Bonus[]) => {
        if (!bonuses || bonuses.length === 0) return

        const { currency, currencyHideCents, buyBonusConfig: config } = useConfigurationStore.getState()
        const { findElementByImageName, cachedImages } = useBuyBonusStore.getState()

        const descriptionPrefix = `${translate('common.promo.congratulations')} ${translate('common.bonusSystem.youHaveBeenAwarded')} `

        if (bonuses.length >= 2) {
            showReactUINotification({
                type: NotificationType.BONUSES_ACTION_MULTIPLE,
                description: (
                    <span>
                        {descriptionPrefix}
                        <Translate
                            components={{
                                b: <b />,
                            }}
                            tKey="common.bonusSystem.notifications.multipleBonusesSuffix"
                            placeholders={{ bonusesAmount: bonuses.length }}
                        />
                    </span>
                ),
                bonuses,
                dismissTimeoutMs: DISMISS_TIMEOUT_MS,
            })

            return
        }

        const singleBonus = bonuses[0]
        const isBonusForCurrentGame = +singleBonus.gin === gin

        const baseBetLabel = isBonusForCurrentGame
            ? ` x ${formatCurrency(+singleBonus.betValue, currency, {
                  hideCents: currencyHideCents,
              })}`
            : ''

        const baseBetLabel_singleOptionFlexi =
            isBonusForCurrentGame && (singleBonus as FreeSpinsBonus | GoldenChipsBonus).flexiBonusOptions
                ? ` x ${formatCurrency(
                      +(singleBonus as FreeSpinsBonus | GoldenChipsBonus).flexiBonusOptions![0].betValue,
                      currency,
                      {
                          hideCents: currencyHideCents,
                      }
                  )}`
                : ''

        switch (singleBonus.type) {
            case BonusType.FREE_SPIN: {
                if (
                    isFlexiBundle(singleBonus) &&
                    singleBonus.flexiBonusOptions &&
                    singleBonus.flexiBonusOptions.length > 1
                ) {
                    showReactUINotification({
                        type: NotificationType.BONUSES_ACTION_FS,
                        description: (
                            <span>
                                {descriptionPrefix}
                                <Translate
                                    components={{
                                        b: <b />,
                                    }}
                                    tKey="common.bonusSystem.notifications.freeSpinsSuffix_FLEXI"
                                    placeholders={{
                                        flexiBonusValue: formatCurrency(singleBonus.flexiBonusValue, currency, {
                                            hideCents: currencyHideCents,
                                        }),
                                    }}
                                />
                            </span>
                        ),
                        bonuses: [singleBonus],
                        dismissTimeoutMs: DISMISS_TIMEOUT_MS,
                    })
                } else if (
                    isFlexiBundle(singleBonus) &&
                    singleBonus.flexiBonusOptions &&
                    singleBonus.flexiBonusOptions.length === 1
                ) {
                    showReactUINotification({
                        type: NotificationType.BONUSES_ACTION_FS,
                        description: (
                            <span>
                                {descriptionPrefix}
                                <Translate
                                    components={{
                                        b: <b />,
                                    }}
                                    tKey="common.bonusSystem.notifications.freeSpinsSuffix"
                                    placeholders={{
                                        remainingSpins: singleBonus.flexiBonusOptions[0].bonusAmount,
                                        bet: baseBetLabel_singleOptionFlexi,
                                    }}
                                    count={+singleBonus.flexiBonusOptions[0].bonusAmount}
                                />
                            </span>
                        ),
                        bonuses: [singleBonus],
                        dismissTimeoutMs: DISMISS_TIMEOUT_MS,
                    })
                } else {
                    showReactUINotification({
                        type: NotificationType.BONUSES_ACTION_FS,
                        description: (
                            <span>
                                {descriptionPrefix}
                                <Translate
                                    components={{
                                        b: <b />,
                                    }}
                                    tKey="common.bonusSystem.notifications.freeSpinsSuffix"
                                    placeholders={{
                                        remainingSpins: singleBonus.remainingAmount ?? 0,
                                        bet: baseBetLabel,
                                    }}
                                    count={+(singleBonus.remainingAmount ?? 0)}
                                />
                            </span>
                        ),
                        bonuses: [singleBonus],
                        dismissTimeoutMs: DISMISS_TIMEOUT_MS,
                    })
                }

                break
            }
            case BonusType.FREE_BUY_BONUS: {
                const freeBuyBonus = modifyFreeBuyBonuses([singleBonus], config.features)

                setFBBonuses(freeBuyBonus)

                const bonusForNotification = freeBuyBonus[0]

                if (!bonusForNotification) return

                const typeConfig = (bonusForNotification as FreeBuyBonusBonus).typeConfiguration
                const fbbConfig = typeConfig?.freeBuyBonusConfiguration

                let resolvedIconStyle: CSSProperties | undefined

                if (fbbConfig) {
                    const matchesActiveBonus = (feature: BuyBonusFeature) =>
                        feature.name === fbbConfig.featureId || feature.guaranteedName === fbbConfig.guaranteedName

                    const selectedFeatureIndex = config.features.findIndex(matchesActiveBonus)

                    const featureImageName = `${FEATURE_IMAGE_PREFIX}${selectedFeatureIndex + 1}.png`

                    let imageData = findElementByImageName(featureImageName)

                    if (!imageData) {
                        imageData = findElementByImageName(FALLBACK_IMAGE_NAME)
                    }

                    if (imageData && cachedImages[imageData.atlasName]) {
                        resolvedIconStyle = buildAtlasImageStyles(
                            imageData.atlasName,
                            imageData.value,
                            cachedImages[imageData.atlasName]
                        )
                    }
                }

                const bonusName = fbbConfig?.label || translate('common.buyBonus')

                showReactUINotification({
                    type: NotificationType.BONUSES_ACTION_FBB,
                    description: (
                        <span>
                            {descriptionPrefix}
                            <Translate
                                components={{
                                    b: <b />,
                                }}
                                tKey="common.bonusSystem.notifications.freeBuyBonusSuffix"
                                placeholders={{ bonusName, bet: baseBetLabel }}
                            />
                        </span>
                    ),
                    bonuses: [bonusForNotification],
                    iconStyle: resolvedIconStyle,
                    dismissTimeoutMs: DISMISS_TIMEOUT_MS,
                })
                break
            }
            case BonusType.COUNTDOWN_FREE_SPIN: {
                let dateTimeString = ''
                let countdownFreespinTimeLabel = ''

                const countdownStrategy =
                    singleBonus.typeConfiguration?.countdownFreeSpinConfiguration?.countdownStrategy

                if (countdownStrategy === CountdownStrategy.CUSTOM) {
                    dateTimeString = formatDateTime(
                        singleBonus.typeConfiguration?.countdownFreeSpinConfiguration?.countdownDateTime ?? '',
                        true
                    )
                } else {
                    const countdown = getCountdownValuesFromBonus(singleBonus, true)

                    if (!countdown) return null

                    const displayItems: CountdownCounterDisplayItems = [
                        { val: countdown.days, label: translate('common.bonusSystem.timeLeftDays') },
                        { val: countdown.hours, label: translate('common.bonusSystem.timeLeftHours') },
                        { val: countdown.minutes, label: translate('common.bonusSystem.timeLeftMinutes') },
                        { val: countdown.seconds, label: translate('common.bonusSystem.timeLeftSeconds') },
                    ]

                    const { firstStringDigits, firstStringLabel, secondStringDigits, secondStringLabel } =
                        getCountdownDisplayItems(displayItems)

                    countdownFreespinTimeLabel = `${firstStringDigits}${firstStringLabel}${secondStringDigits}${secondStringLabel}`
                }

                showReactUINotification({
                    type: NotificationType.BONUSES_ACTION_CFS,
                    description: (
                        <span>
                            {descriptionPrefix}
                            <Translate
                                components={{
                                    b: <b />,
                                }}
                                tKey={
                                    countdownStrategy === CountdownStrategy.CUSTOM
                                        ? 'common.bonusSystem.notifications.countdownFreeSpinsSuffix_CUSTOM'
                                        : 'common.bonusSystem.notifications.countdownFreeSpinsSuffix_RELATIVE_TO_PLAY_NOW'
                                }
                                placeholders={{
                                    bet: baseBetLabel,
                                    timeLabel:
                                        countdownStrategy === CountdownStrategy.CUSTOM
                                            ? dateTimeString
                                            : countdownFreespinTimeLabel,
                                }}
                            />
                        </span>
                    ),
                    bonuses: [singleBonus],
                    dismissTimeoutMs: DISMISS_TIMEOUT_MS,
                })
                break
            }
            case BonusType.GOLDEN_CHIP: {
                showReactUINotification({
                    type: NotificationType.BONUSES_ACTION_GC,
                    description: (
                        <span>
                            {descriptionPrefix}
                            {isFlexiBundle(singleBonus) ? (
                                <Translate
                                    components={{
                                        b: <b />,
                                    }}
                                    tKey="common.bonusSystem.notifications.goldenChipsSuffix_FLEXI"
                                    placeholders={{
                                        flexiBonusValue: formatCurrency(singleBonus.flexiBonusValue, currency, {
                                            hideCents: currencyHideCents,
                                        }),
                                    }}
                                />
                            ) : (
                                <Translate
                                    components={{
                                        b: <b />,
                                    }}
                                    tKey="common.bonusSystem.notifications.goldenChipsSuffix"
                                    placeholders={{
                                        remainingChips: singleBonus.remainingAmount ?? 0,
                                        bet: baseBetLabel,
                                    }}
                                    count={+(singleBonus.remainingAmount ?? 0)}
                                />
                            )}
                        </span>
                    ),
                    bonuses: [singleBonus],
                })
                break
            }
            default:
                break
        }
    }

    const handlePlayNow = (bonus?: Bonus) => {
        if (!bonus) return

        const isSingleOptionFlexi = isFlexiBundle(bonus) && isSingleOptionFlexiBundle(bonus)

        const payload: BonusesPlayNowMessage['payload'] = {
            bonusIds: [bonus.id],
            ...(showFlexiBundles &&
            isSingleOptionFlexi &&
            (bonus as FreeSpinsBonus | GoldenChipsBonus).flexiBonusOptions
                ? { flexiBonusOption: (bonus as FreeSpinsBonus | GoldenChipsBonus).flexiBonusOptions![0] }
                : {}),
        }

        const message: BonusesPlayNowMessage = {
            type: GameUIMessageType.BONUSES_PLAY_NOW,
            payload,
        }

        window.uiBus.postMessage(message)

        switch (bonus.type) {
            case BonusType.FREE_BUY_BONUS: {
                setHasFBBActivatedBonus(true)
                setFBBActiveBonus(bonus)
                setFBBonusSystemState(BonusSystemState.IN_PLAY)

                if (hasBuyBonus && buyBonusConfig?.shouldTheFeatureStayActive) {
                    const freeBuyBonusConfig = bonus.typeConfiguration?.freeBuyBonusConfiguration

                    const matchesActiveBonus = (feature: BuyBonusFeature) =>
                        feature.name === freeBuyBonusConfig?.featureId ||
                        feature.guaranteedName === freeBuyBonusConfig?.guaranteedName

                    const selectedFeatureIndex = buyBonusConfig.features.findIndex(matchesActiveBonus)
                    const currentFeature =
                        selectedFeatureIndex !== -1 ? buyBonusConfig.features[selectedFeatureIndex] : null

                    setSelectedFeatureIndex(selectedFeatureIndex)
                    setSelectedFeature(currentFeature)
                    updateCurrentFeatureMultiplier(currentFeature?.multiplier ?? 1)
                    setMultiChoiceToggle(true)
                }

                break
            }
            case BonusType.FREE_SPIN: {
                setFSActiveBonus(bonus)
                setFSBonusSystemState(BonusSystemState.IN_PLAY)
                break
            }
            case BonusType.COUNTDOWN_FREE_SPIN: {
                setCFSActiveBonus(bonus)
                setCFSBonusSystemState(BonusSystemState.IN_PLAY)
                break
            }
            default:
                break
        }
    }

    return { handlePlayNow, showBonusAwardedNotification }
}

export default useBonusSystem
